import type { TOC } from '@ember/component/template-only'
import type ManageTeachersRoute from 'teacher-ui-client/manage/teachers/route'
import type ManageTeachersController from 'teacher-ui-client/manage/teachers/controller'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { RolloverHeaderReturnLink } from 'teacher-ui-client/components/rollover/header-return-link'
import { t, formatDate } from 'ember-intl'
import { usesExternalProvisioning } from 'teacher-ui-client/helpers/uses-external-provisioning'
import { usesExternalRostering } from 'teacher-ui-client/helpers/uses-external-rostering'
import { and, eq, or } from 'ember-truth-helpers'
import { TilesManageActions } from 'teacher-ui-client/components/tiles/manage-actions'
import { AddTeacherToSchool } from 'teacher-ui-client/components/form-wrapper/add-teacher-to-school'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { on } from '@ember/modifier'
import { LayoutBlocksModal } from 'teacher-ui-client/components/layout-blocks/modal'
import { AddTeachersDialog } from 'teacher-ui-client/components/add-teachers-dialog'
import { fn, array } from '@ember/helper'
import { usesExternalAuthentication } from 'teacher-ui-client/helpers/uses-external-authentication'
import { DataOperationsTable } from 'teacher-ui-client/components/data-operations/table'
import perform from 'ember-concurrency/helpers/perform'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { pick } from 'teacher-ui-client/helpers/pick'
import { includes } from 'teacher-ui-client/helpers/includes'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { DeleteTeachersPermanentlyForm } from 'teacher-ui-client/components/form-wrapper/delete-teachers-permanently-form'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { SortType } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import RouteTemplate from 'ember-route-template'
import type { TeacherViewModel } from 'teacher-ui-client/manage/teachers/controller'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import { OneRosterNotice } from 'teacher-ui-client/components/manage/one-roster/notice'
import canAccess from 'teacher-ui-client/helpers/can-access'
import { ColumnSortIcon } from 'teacher-ui-client/components/data-operations/table/column-sort-icon'

interface Signature {
  Args: {
    model: ModelFor<ManageTeachersRoute>
    controller: ManageTeachersController
  }
}

function getTeachers(items: TeacherViewModel[]) {
  return items.map((item) => item.teacherRecord)
}

const oneRosterUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/manage-teachers')

export class ManageTeachersPage extends Component<Signature> {
  @service featureControl!: FeatureControlService

  get showOneRosterTemplate() {
    return this.featureControl.canAccess('one_roster')
  }

  <template>
    <PageHeader>
      <:title>
        <h1>
          {{t "manage.teachers.title"}}
        </h1>
      </:title>
      <:left>
        <RolloverHeaderReturnLink />
      </:left>
    </PageHeader>
    {{#if this.showOneRosterTemplate}}
      <OneRosterManageTeachers />
    {{else}}
      <ManageTeachers @model={{@model}} @controller={{@controller}} />
    {{/if}}
  </template>
}

const OneRosterManageTeachers: TOC<unknown> = <template>
  <OneRosterNotice @linkUrl={{oneRosterUrl}} @linkText={{t "manage.3essentials.marketingTile.goToTeachers"}} />
</template>

const ManageTeachers: TOC<Signature> = <template>
  {{! If both of these flags are true, there'll be nothing in the tile, so hide the entire tile }}
  {{#unless (and (usesExternalProvisioning) (usesExternalRostering))}}
    <TilesManageActions
      data-test-manage-actions-tile
      class="mb-6 p-4"
      @themeKey={{themeKey @model.subscriptionType}}
      @title={{t "manage.bulkActions.addToYourSchool" name=(t "manage.teachers.namePlural")}}
      as |tile|
    >
      {{#unless (usesExternalProvisioning)}}
        <tile.formArea class="max-w-3xl">
          <h5>
            {{t "manage.singularActions.create.label" name=(t "manage.teachers.nameSingular")}}
          </h5>
          <AddTeacherToSchool
            data-test-create-teachers
            @school={{@model.school}}
            @subscriptionType={{@model.subscriptionType}}
            @onSelectCsvFile={{@controller.selectCsvFile}}
            @isUploading={{@controller.teacher.importFromCsv.isRunning}}
          />
        </tile.formArea>
      {{/unless}}
      <tile.actionsArea>
        {{#unless (usesExternalRostering)}}
          <UiButton data-test-add-existing-teacher {{on "click" @controller.addExisting}}>
            <span class="my-1 flex items-center">
              <PhosphorIcon class="mr-2 h-8 w-8 {{@controller.iconColorClass}}" @icon="phosphor-user-plus-fill" />
              {{t "manage.bulkActions.addExisting.label" name=(t "manage.teachers.namePlural")}}
            </span>
          </UiButton>
        {{/unless}}
      </tile.actionsArea>
    </TilesManageActions>

    <LayoutBlocksModal
      @title={{t "manage.bulkActions.addExisting.label" name=(t "manage.teachers.namePlural")}}
      @showModal={{@controller.isShowingSelectTeachers}}
      @close={{fn @controller.showAddExistingTeachersModal false}}
    >
      <AddTeachersDialog
        @subscriptionType={{@model.subscriptionType}}
        @teachers={{@controller.teachersWithoutSchoolClass}}
        @addWithTrialAction={{@controller.addExistingWithTrial}}
        @addWithSubAction={{@controller.addExistingWithSub}}
        @hideLogin={{(usesExternalAuthentication)}}
      />
      {{! Provide a gap between the content bottom of modal }}
      <div class="mb-4" />
    </LayoutBlocksModal>
  {{/unless}}
  {{#if @model.teachers}}
    <DataOperationsTable
      @dataOperationsConfig={{@controller.dataOperationsConfig}}
      as |selectRecords sorting pagination|
    >
      <div class="border-dusty-black-100 overflow-visible rounded-md border">
        <div class="{{@controller.tableTheme.controlsBg}} safari-gap-2 flex gap-2 p-5">
          {{#unless (usesExternalRostering)}}
            <UiButton
              data-test-delete-selected-teachers
              disabled={{or selectRecords.noneSelected (canAccess "one_roster")}}
              {{on "click" (fn @controller.showDeleteTeachersModal true)}}
            >
              {{t "manage.bulkActions.destroy.label"}}
            </UiButton>
          {{/unless}}
          {{#unless (usesExternalAuthentication)}}
            <UiButton
              data-test-bulk-password-reset
              disabled={{selectRecords.noneSelected}}
              {{on "click" (perform @controller.emailPasswordReset selectRecords.selectedIds)}}
            >
              {{t "manage.teachers.bulkActions.emailPasswordReset.label"}}
            </UiButton>
            {{#unless (eq @model.subscriptionType "writing")}}
              <UiButton
                data-test-bulk-parent-letters
                disabled={{@controller.noStudentsSelected}}
                {{on "click" (perform @controller.parentLettersTask)}}
              >
                {{t "manage.teachers.bulkActions.parentLetters.label"}}
              </UiButton>
            {{/unless}}
          {{/unless}}
          <UiButton
            data-test-bulk-student-logins
            @disabled={{@controller.noStudentsSelected}}
            {{on "click" (fn @controller.printStudentLoginsForTeachers selectRecords.selectedRecords)}}
          >
            {{t "manage.teachers.bulkActions.printLogins.label"}}
          </UiButton>
          <InputText
            class="ml-auto"
            type="search"
            role="searchbox"
            name="search-filter"
            value={{@controller.textFilter}}
            aria-label={{t "manage.manageTeachers.filterTeachers.label"}}
            placeholder={{t "manage.manageTeachers.filterTeachers.placeholder"}}
            @themeKey={{themeKey @model.subscriptionType}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" @controller.setTextFilter)}}
          />
        </div>
        <table data-test-manage-teachers-table class="w-full border-t border-white">
          <thead class={{@controller.tableTheme.headerBg}}>
            <tr class="divide-x divide-white border-b border-white">
              <th class="w-14 p-3 text-center">
                <label class="cursor-pointer">
                  {{t "manage.selectAllLabel"}}
                  <input
                    data-test-select-all-teachers
                    type="checkbox"
                    class="cursor-pointer"
                    checked={{selectRecords.allSelected}}
                    {{on "change" selectRecords.toggleAll}}
                  />
                </label>
              </th>
              <th class="max-w-32">
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "firstName")}}
                >
                  {{t "manage.firstName"}}
                  <ColumnSortIcon @columnName="firstName" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
                </button>
              </th>
              <th class="max-w-32">
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "lastName")}}
                >
                  {{t "manage.lastName"}}
                  <ColumnSortIcon @columnName="lastName" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
                </button>
              </th>
              {{#unless (usesExternalAuthentication)}}
                <th data-test-login-header>
                  <button
                    type="button"
                    class="w-full px-3 py-6 text-left whitespace-nowrap"
                    {{on "click" (fn sorting.sort SortType.ByValue "login")}}
                  >
                    {{t "manage.login"}}
                    <ColumnSortIcon @columnName="login" @sortCol={{sorting.sortCol}} @sortDir={{sorting.sortDir}} />
                  </button>
                </th>
              {{/unless}}
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByNumber "studentCount")}}
                >
                  {{t "manage.students.namePlural"}}
                  <ColumnSortIcon
                    @columnName="studentCount"
                    @sortCol={{sorting.sortCol}}
                    @sortDir={{sorting.sortDir}}
                  />
                </button>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "schoolClassNames")}}
                >
                  {{t "manage.schoolClasses.namePlural"}}
                  <ColumnSortIcon
                    @columnName="schoolClassNames"
                    @sortCol={{sorting.sortCol}}
                    @sortDir={{sorting.sortDir}}
                  />
                </button>
              </th>
              <th>
                <button
                  type="button"
                  class="w-full px-3 py-6 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByDate "trialEndDate")}}
                >
                  {{t "manage.subscriptions.trialEndDate"}}
                  <ColumnSortIcon
                    @columnName="trialEndDate"
                    @sortCol={{sorting.sortCol}}
                    @sortDir={{sorting.sortDir}}
                  />
                </button>
              </th>
              <th aria-label={{t "manage.edit"}} class="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {{#each pagination.currentPageRecords as |row|}}
              {{#let (eq @controller.editingRecordId row.id) as |isEditing|}}
                <tr
                  data-test-record-row={{row.id}}
                  class={{if isEditing @controller.tableTheme.bodyRowBgEditing @controller.tableTheme.bodyRowBg}}
                >
                  <td class="w-14 p-3 text-center">
                    <input
                      type="checkbox"
                      class="cursor-pointer"
                      checked={{includes selectRecords.selectedIds row.id}}
                      aria-label={{t "manage.manageClasses.selectSingleRecordLabel" name=row.fullName}}
                      {{on "change" (fn selectRecords.toggle row.id)}}
                    />
                  </td>
                  <td class="max-w-32 p-3 wrap-break-word {{if isEditing 'font-bold'}}">
                    {{row.firstName}}
                  </td>
                  <td class="max-w-32 p-3 wrap-break-word {{if isEditing 'font-bold'}}">
                    {{row.lastName}}
                  </td>
                  {{#unless (usesExternalAuthentication)}}
                    <td class="p-3 {{if isEditing 'font-bold'}} break-all">{{row.login}}</td>
                  {{/unless}}
                  <td class="p-3 {{if isEditing 'font-bold'}}">
                    {{row.studentCount}}
                  </td>
                  <td class="p-3 {{if isEditing 'font-bold'}}">
                    {{formatValue row.schoolClassNames replacement=(t "manage.noClassAssigned")}}
                  </td>
                  <td class="p-3 {{if isEditing 'font-bold'}}">
                    <span class={{if row.trialSub.isExpired "text-watermelony-red-350"}}>
                      {{#if row.trialSub.endDate}}
                        {{formatDate row.trialSub.endDate day="2-digit" month="long" year="numeric"}}
                      {{else}}
                        {{t "manage.na"}}
                      {{/if}}
                    </span>
                  </td>
                  <td class="p-3">
                    {{#if isEditing}}
                      <UiButtonLinkTo
                        data-test-edit-teacher-close
                        class="font-normal whitespace-nowrap"
                        @route="manage.teachers"
                      >
                        <PhosphorIcon @icon="phosphor-x-bold" class="mr-2" />
                        {{t "manage.close"}}
                      </UiButtonLinkTo>
                    {{else}}
                      <UiButtonLinkTo
                        data-test-edit-teacher-open
                        class="font-normal whitespace-nowrap"
                        @route="manage.teachers.teacher"
                        @models={{array row.id}}
                      >
                        <PhosphorIcon @icon="phosphor-note-pencil-fill" class="mr-2" />
                        {{t "manage.edit"}}
                      </UiButtonLinkTo>
                    {{/if}}
                  </td>
                </tr>
                {{#if isEditing}}
                  <tr>
                    <td
                      colspan={{if (usesExternalAuthentication) "7" "8"}}
                      class={{if isEditing "bg-juicy-orange-50 p-3"}}
                    >
                      {{outlet}}
                    </td>
                  </tr>
                {{/if}}
              {{/let}}
            {{else}}
              <tr>
                <td colspan={{if (usesExternalAuthentication) "7" "8"}} class="p-3 text-center italic">
                  {{t "manage.noDataAvailable"}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
      <LayoutBlocksModal
        @title={{t "manage.manageTeachers.bulkActions.deleteTeachers.title" count=@controller.selectedIds.length}}
        @showModal={{@controller.deleteTeachersOpen}}
        @close={{fn @controller.showDeleteTeachersModal false}}
      >
        <DeleteTeachersPermanentlyForm
          data-test-delete-teachers-modal
          @teachersWhoCanBeDeleted={{@controller.teachersWhoCanBeDeleted}}
          @teachersWhoCantBeDeleted={{@controller.teachersWhoCantBeDeleted}}
          @teachers={{getTeachers selectRecords.selectedRecords}}
          @onSubmit={{@controller.submitDeleteTeachersModal}}
          @unselectAction={{@controller.unselectAllTeachers}}
          @closeModalAction={{fn @controller.showDeleteTeachersModal false}}
          @subscriptionType={{@model.subscriptionType}}
        />
      </LayoutBlocksModal>
    </DataOperationsTable>
  {{else}}
    <NoDataMessage />
  {{/if}}
</template>

export default RouteTemplate(ManageTeachersPage)
